// 自营 b 权益购买展示模块
import {
  Vue, Component, Watch, Prop,
} from 'vue-property-decorator';
import { Toast } from '@hb/coffee';
import { namespace } from 'vuex-class';
import type {
  ApplyChannelData, ProductCapitalItem, CalcPlanListData,
} from '@/api/apply-product/types';
import { EquityPathType } from '@/api/apply-product/types';

import { HoldModel } from '@/shared/decorators/hold-model';
import {
  RightSummaryType,
  RightCheckResultType,
  ContractListSceneEnum,
  RightPackageSourceEnum,
  RightPackageTypeEnum,
  AutrakyProcessExpEnum,
  LoanCardTypeEnum,
  GalileoExpStyleEnum,
} from '@/api/rights/types';

import ProductCommodity from '@/shared/commodity';
import { ubtCheck } from '@/shared/right-utils/rights-ubt-tool';
import {
  LoanRightStateType,
  PeriodOption,
  SpeedRightsType,
} from '@/views_v2/apply-product/types';
import { customReportZiyingRightTabCheckBreak } from '@/views_v2/apply-product/product';
import { ForceReadService } from '@/widgets/right-contract';
import { PricingTypeEnum, RightsModelTypeEnum, isEquityRequiredForPricingType } from '@/enums/rights';
import { showOutSideContractList } from './showOutSideContractList';

// 这些组件现在在 RightsCardContent.vue 中使用
import { showCouponDiscountPopup, destroyCouponDiscountPopup } from './autarkyRetainRightsTest/couponDiscountPopupService';
import { showTabChangeRetain } from './tabChangeRightsRetain/showTabChangeRetain';
import { destroyPopupA } from './tabChangeRightsRetain/popupAService';
import { destroyPopupB } from './tabChangeRightsRetain/popupBService';
import RightsTabContainer from './allRightsCardStyle/RightsTabContainer.vue';
import RightsCardContent from './allRightsCardStyle/RightsCardContent.vue';
import NormalTabContent from './allRightsCardStyle/NormalTabContent.vue';
import StyleA from './allRightsCardStyle/autarkyCardStyleTest/styleA.vue';
import StyleB from './allRightsCardStyle/autarkyCardStyleTest/styleB.vue';
import SpeedTabText from './allRightsCardStyle/autarkyCardStyleTest/SpeedTabText.vue';

const style = require('../index.module.scss') as Dictionary;

const ApplyProductStore = namespace('applyProduct');
const LoanRightStore = namespace('loanRight');

  @Component
export default class ZiyingBRightsTab extends Vue {
    // 是否h5外采活体回跳
    @Prop(Boolean) readonly h5Bio!: boolean;

    // 是否一键绑卡回跳
    @Prop(Object) quickBindCardResult!: Dictionary;

    // 权益类型
    @Prop(Number) readonly pricingType?: PricingTypeEnum;

    @ApplyProductStore.State channel!: ApplyChannelData | undefined;

    @ApplyProductStore.State rightCheckStatus!: RightCheckResultType | null;

    @ApplyProductStore.State selectRightInCapital!: ProductCapitalItem | null;

    @ApplyProductStore.State planListData!: CalcPlanListData | null;

    @ApplyProductStore.State period!: PeriodOption | undefined;

    @ApplyProductStore.State autrakyProcessExpRes!: AutrakyProcessExpEnum;

    @LoanRightStore.State rightParams!: LoanRightStateType['rightParams'];

    @LoanRightStore.State showSpeed!: LoanRightStateType['showSpeed'];

    @LoanRightStore.State isRightTick!: LoanRightStateType['isRightTick'];

    @LoanRightStore.State recordCountDownTime!: LoanRightStateType['recordCountDownTime'];

    @LoanRightStore.State isCountingDownFinished!: LoanRightStateType['isCountingDownFinished'];

    @ApplyProductStore.State is24NeedRightAndNormalIs36!: boolean;

    @ApplyProductStore.Getter isDoublePassageDefaultSelectNormal!: boolean;

    @ApplyProductStore.Getter showCouponRetainPopupExperiment!: boolean;

    @ApplyProductStore.Getter isCouponLimitExperiment!: boolean;

    @ApplyProductStore.Action getMaxAmount!: () => Promise<void>;

    @LoanRightStore.Action getRightSummaryData!: (
      payload?:{ planListData?: CalcPlanListData | null }
    ) => void;

    @ApplyProductStore.Action $updateState!: (data: Dictionary) => void;

    @ApplyProductStore.Action checkDualChannelLight36Mode!: (payload?: {
      vueInstance?: { $root: { $emit: (event: string, data?: unknown) => void } }
    }) => Promise<boolean>;

    @ApplyProductStore.Action checkSwitchBackToSpeedChannel!: (payload?: {
      vueInstance?: { $root: { $emit: (event: string, data?: unknown) => void } }
    }) => Promise<boolean>;

    @LoanRightStore.Action('$updateState') updateLoanRightState!: (data: Dictionary) => void;

    @HoldModel()
    private rightscheckTipsShow!: boolean;

    private zxCardAppContractInstance = ForceReadService({
      packageType: RightPackageTypeEnum.CHANNEL_B,
      forceReadFromScene: RightPackageSourceEnum.LOAN_APPLY,
    });

    private get isAutarky() {
      return this.channel
        && this.channel.productId === ProductCommodity.AUTARKY_B_COMMODITY;
    }

    /** 是否为双融担模式（当前仅自营渠道支持） */
    private get isDoubleIntegrateMode(): boolean {
      const { capitalList } = this.channel || {};

      return !!(capitalList && capitalList.length);
    }

    /** 双融担是否有权益 */
    private get doubleIntegrateHasRight(): boolean {
      const { capitalList = [] } = this.channel || {};

      /** 双融担模式使用capitalList[0].needRight判断有无权益  */
      return !!(this.isDoubleIntegrateMode && capitalList[0].needRight);
    }

    /** 是否需要购买权益
     * 1. 自营b根据channel.needRight来判断
     * 2. 联营产品根据营销的接口来判断
     */
    private get needRight(): boolean {
      // 24+单通道普通通道切换到轻资产场景，需要展示购买权益模块
      if (this.is24NeedRightAndNormalIs36) {
        return true;
      }
      if (this.isAutarky) {
        const { needRight = false } = this.channel || {};
        /** 兼容老版本的needRight结构 */
        return !!(this.isDoubleIntegrateMode ? this.doubleIntegrateHasRight : needRight);
      }

      const { canPurchase } = this.rightCheckStatus || {};
      return !!canPurchase;
    }

    // 计算当前的equaty值
    public get equatyValue(): EquityPathType {
      if (this.isDoubleIntegrateMode) {
        const { capitalList = [] } = this.channel || {};

        /** 双融担模式资方都无权益，直接返回 2 */
        if (this.needRight === false) {
          return EquityPathType.NOT_NEED_RIGHT;
        }

        /** 单资方: 选择"普通权益"为 0，选择"极速权益"为 1  */
        if (capitalList.length === 1) {
          return this.canSubmitOrder ? EquityPathType.BUY_RIGHTS : EquityPathType.NORMAL;
        // eslint-disable-next-line no-else-return
        } else {
          /** 双资方: 选择"24资方&有权益" 为 1，选择"24资方&无权益、36资方"或"24+权益+纯24双承接模式"为 2 */
          return this.channel?.needRight
            ? EquityPathType.BUY_RIGHTS : EquityPathType.NOT_NEED_RIGHT;
        }
      }

      // 存在该字段且等于false，传2
      if (this.needRight === false) {
        // 联营产品 - needRight === false,  equatyValue = EquityPathType.NONE
        return this.isAutarky ? EquityPathType.NOT_NEED_RIGHT : EquityPathType.NONE;
      }

      // 存在该字段且等于true，
      if (this.canSubmitOrder) {
        return EquityPathType.BUY_RIGHTS;
      }

      // 定价标签为5 && equityPath为''时，改为2
      // 为了和自营保持一致，这块的逻辑计算比较复杂后续不应该放在前端维护
      return (this.pricingType === PricingTypeEnum.LOAN_24_MUST_RIGHTS_OR_LOAN_36)
        ? EquityPathType.NOT_NEED_RIGHT : EquityPathType.NORMAL;
    }

    /** 是否已经确认弹窗 */
    public isChecked = false;

    /** 当臻享卡勾选状态发生变更时，将勾选协议提示取消 */
    private async checkChange() {
      this.isChecked = !this.isChecked;

      const { contractInfo } = this.rightSummary || {};
      if (this.isChecked) {
        this.rightscheckTipsShow = false;
        this.$ubtPageButtonNew('finance_loan_apply_right_agreementCheck', this.tqParams);

        if (contractInfo.isUseContractList) {
          this.isChecked = true;
        } else {
          this.isChecked = await this.zxCardAppContractInstance?.handleForceRead({
            contractScene: this.rightParams?.contractInfo.scene || '',
            tqParams: this.tqParams,
            rightsModelType: this.rightSummary.rightsModelType,
          });
        }
      }

      this.updateLoanRightState({
        isRightTick: this.isChecked,
      });
    }

    // 是否达到限额，不能再购买
    private isFull = false;

    /** 自营权益限额检测结果 */
    private ziyingRightsCheckResult: Dictionary = {};


    public get rightSummary(): RightSummaryType {
      if (this.rightParams) {
        return this.rightParams;
      }
      return {
        packageId: '',
        supplierCode: '',
        packageCode: '',
        packageType: RightPackageTypeEnum.CHANNEL_B,
        packageName: '',
        originalPrice: 0,
        sellPrice: 0,
        saveMoney: 0,
        contractInfo: {
          name: '',
          scene: ContractListSceneEnum.DEBIT_PAYMENT_SCENE,
        },
        productId: '',
        countDownTime: 0,
        rightsModelType: RightsModelTypeEnum.ORIGINAL,
      };
    }

    /** 计算当前埋点上报时权益包的字段属性 */
    public get tqParams(): Dictionary {
      const {
        packageId, sellPrice, supplierCode, packageName, packageType, extraInfo,
      } = this.rightSummary || {};

      const { productId } = this.channel || {};
      const { rateType, capitalCode } = this.selectRightInCapital || {};
      // 降价人群标签 用户标签
      const { userTag, userLoanRate24ZyUsr } = this.channel || {};
      // 综合利率
      const { rateValue } = this.planListData || {};

      ubtCheck({
        packageId,
        sellPrice,
        supplierCode,
        packageName: packageName || '',
        packageType: packageType || 0,
        from: RightPackageSourceEnum.LOAN_APPLY,
      });

      return {
        packageId,
        sellPrice,
        supplierCode,
        packageName: packageName || '',
        packageType: packageType || 0,
        from: RightPackageSourceEnum.LOAN_APPLY,
        productId,
        rateType: (this.isAutarky && rateType) || '',
        capitalCode: (this.isAutarky && capitalCode) || '',
        userTag,
        userLoanRate24ZyUsr,
        rateValue,
        pricingType: this.pricingType,
        extraInfo,
      };
    }

    /** 初始化权益tab是否在极速状态 */
    private initSpeedStatus() {
      let showSpeed = false;
      // 自营
      if (this.isAutarky) {
        // 达到限额时 、双融担模式 默认选择"普通通道"的实验用户
        showSpeed = !this.isDoublePassageDefaultSelectNormal;
        /** 更新资方code */
        this.updateSelectRightInCapital(showSpeed);
      } else {
        // 联营
        if (this.rightCheckStatus) {
          showSpeed = !!this.rightCheckStatus.canPurchase;
        }
        // 24+单通道普通通道切换到轻资产场景，默认选择普通通道
        if (this.is24NeedRightAndNormalIs36) {
          showSpeed = false;
        }
      }

      this.updateLoanRightState({
        showSpeed,
      });
    }


    private selectCapitalInDoubleIntegrate(selectRightCapital: boolean): ProductCapitalItem | null {
      const { capitalList = [] } = this.channel || {};

      /** 服务接口已排序：24+权益的 资方排在第一位 */
      if (selectRightCapital) {
        return capitalList[0];
      }

      /** 获取无权益资方：若是多资方，则返回第二位资方，若是单资方则返回第一位资方 */
      return capitalList.length > 1 ? capitalList[1] : capitalList[0];
    }

    /** 双融担-更新选择的资方 */
    private updateSelectRightInCapital(selectRight: boolean): void {
      /** 双融担 + 有权益模式，tab切换同步选择对应资方 */
      if (this.isDoubleIntegrateMode && this.doubleIntegrateHasRight) {
        const targetCapital = this.selectCapitalInDoubleIntegrate(selectRight);

        this.$updateState({
          selectRightInCapital: {
            ...targetCapital,
          },
        });
      }
    }

    // 切换权益 tab
    public async onTabChange(type: SpeedRightsType):Promise<void> {
      if (type === SpeedRightsType.SPEED) {
        // 判断切换回极速通道是否需要切回自营渠道
        const needSwitchBack = await this.checkSwitchBackToSpeedChannel({
          vueInstance: this,
        });

        // 如果没有切回自营渠道，则继续执行正常的极速权益逻辑
        if (!needSwitchBack) {
          if (this.isFull) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            Toast('臻享权益筹备中 敬请期待');
          } else {
            this.updateLoanRightState({
              showSpeed: true,
            });
            this.getRightSummaryData({ planListData: this.planListData });
            //  此处代码是为了解决点击切换权益tab时的埋点资方数据的准确性
            const { rateType = '' } = this.selectCapitalInDoubleIntegrate(true) || {};
            this.$ubtPageButtonNew('fincance_inloan_speedapply_click', { ...this.tqParams, rateType });
          }
        }
      } else {
        //  此处代码是为了解决点击切换权益tab时的埋点资方数据的准确性
        const { rateType = '' } = this.selectCapitalInDoubleIntegrate(false) || {};
        this.$ubtPageButtonNew('fincance_inloan_normalapply_click', { ...this.tqParams, rateType });

        // 判断是否满足24+权益单通道切换普通通道需使用轻资产承接场景
        const canSwitchToNormal = await this.checkDualChannelLight36Mode({
          vueInstance: this,
        });
        if (!canSwitchToNormal) {
          // 不能切换到普通通道，暂停切换操作
          return;
        }

        // 命中优惠券挽留弹窗实验 && 券优惠金额大于0，展示挽留弹窗
        if (this.showCouponRetainPopupExperiment && this.planListData?.couponDiscountAmount) {
          const res = await showCouponDiscountPopup();
          destroyCouponDiscountPopup();
          /** 如果用户选择保留优惠券，则不进行普通通道切换 */
          if (res.success) {
            return;
          }
        } else {
          // 如果权益参数存在
          if (this.rightParams) {
            // 获取用户选择结果
            const res = await showTabChangeRetain({
              rightParams: this.rightParams,
              goDetail: () => {
                if (this.rightParams) {
                  this.showRightsDetail(this.rightParams);
                }
              },
              contractPreview: () => {
                this.openPopupContract();
              },
            });
            // retainSuccess: true 表示用户选择保留权益，false 表示用户选择放弃权益
            // submitClick: true 表示需要帮用户提交订单，false 表示不需要帮用户提交订单
            const { retainSuccess, submitClick } = res;
            if (retainSuccess) {
              // 如果用户在样式b 选择保留权益，则需要帮用户提交订单
              if (submitClick) {
                // 更新勾选状态
                this.isChecked = true;
                this.updateLoanRightState({
                  isRightTick: this.isChecked,
                });
                // 提交订单
                this.$emit('submitClick');
              }
              return;
            }
          }
        }
        this.updateLoanRightState({
          showSpeed: false,
        });
      }

      /** 切换权益tab时，主要是为了24+权益切换tab时需要重新请求试算接口，向外抛出钩子 */
      this.$emit('rightTabChange', type);
      // 命中优惠券限制实验会切换右侧通道会隐藏临时额度，需要重新获取最大可提现金额
      if (this.isCouponLimitExperiment) {
        this.getMaxAmount();
      }
      // session.set(storageKey, this.showSpeed);
    }

    private showRightsDetail(rightSummary: RightSummaryType) {
      this.$ubtPageButtonNew('fincance_inloan_detail_click', this.tqParams);
      this.$emit('showRightsDetail', rightSummary);
    }

    // 监听 tab 值变化，true 时，获取权益相关信息
    @Watch('showSpeed')
    private getSpeedData(newValue: boolean) {
      /** 双融担 + 有权益模式，tab切换同步选择对应资方 */
      this.updateSelectRightInCapital(newValue);

      // 埋点曝光逻辑: 只需要在买臻享卡卡片购买的情况曝光
      if (this.tqParams.supplierCode && this.showRights) {
        // 初始化曝光
        this.$ubtModuleExposeNew('finance_loan_apply_right_expose', { ...this.tqParams, showSpeed: this.showSpeed });
      }
    }

    private async openPopupContract() {
      await this.zxCardAppContractInstance?.handleForceRead({
        contractScene: this.rightParams?.contractInfo.scene || '',
        tqParams: this.tqParams,
        preview: true,
        rightsModelType: this.rightSummary.rightsModelType,
      });
    }

    /** 打开合同弹窗 */
    private async openContract() {
      this.$ubtPageButtonNew('finance_loan_apply_right_agreement', this.tqParams);
      const { contractInfo } = this.rightSummary || {};
      if (contractInfo) {
        // 如果需要使用外部同列表，则使用合同列表
        if (contractInfo.isUseContractList && contractInfo.contractList?.length) {
          showOutSideContractList(contractInfo);
        } else {
          const handleRes = await this.zxCardAppContractInstance?.handleForceRead({
            contractScene: this.rightParams?.contractInfo.scene || '',
            tqParams: this.tqParams,
            preview: true,
            rightsModelType: this.rightSummary.rightsModelType,
          });
          // 只有勾选时再改变勾选状态
          if (handleRes) {
            this.isChecked = handleRes;
          }
        }
      }
    }

    /** 显示权益模块 */
    public get showRights(): boolean {
      const { packageType, loanCardType } = this.rightSummary || {};

      if (packageType === RightPackageTypeEnum.ZHEN_XIANG_MEMBER_CARD) {
        return true;
      }

      if (packageType === RightPackageTypeEnum.CHANNEL_B && loanCardType) {
        return true;
      }

      return false;
    }

    /** 提现下单时 选择臻享权益，需要权益下单 */
    public get canSubmitOrder(): boolean {
      return this.showRights && this.showSpeed && Object.keys(this.rightSummary).length !== 0;
    }

    /**
     * 判断当前用户是否命中了折扣豁免，针对命中折扣豁免的用户，在产品的展示上会基于接口返回的 countDownTime做如下处理：
     * 如果返回 countDownTime，则展示优惠价和倒计时；
     * 如果不返回 countDownTime，则展示限时和优惠价；
     *  */
    private get isRightDiscountUser() {
      const { sellPrice, originalPrice } = this.rightSummary;
      return originalPrice && sellPrice !== originalPrice && !this.isCountingDownFinished;
    }


    /** 判断是否需要购买权益，以及能购买极速模式 */
    @Watch('channel')
    private needRightCheck(newChannel: ApplyChannelData, oldChannel: ApplyChannelData) {
      /**
       * 这段曝光逻辑是为了解决在切换权益tab时，例如从联营臻享卡切换到自营臻享卡时，只会有渠道变更后
       * 将权益包等数据重新赋值，而不会经历卡片挂载跟销毁，外部的needbuyright的值不会变化，始终为true
       * 所以需要手动触发曝光埋点
       * 代码逻辑上该卡片并没有实际二次曝光，只是根据数据刷新重新赋值，但业务上需要此曝光埋点二次触发
       */
      if (oldChannel && newChannel && newChannel.productId !== oldChannel.productId) {
        this.$ubtModuleExposeNew('finance_loan_apply_right_expose', {
          ...this.tqParams,
          showSpeed: this.showSpeed,
        });
      }

      /** 若channel非 整个渠道的更新(双融担-权益tab切换)，则无需更新 */
      if (oldChannel && newChannel && newChannel.productId === oldChannel.productId) {
        /** 上报自定义埋点，为后续异常排查 */
        customReportZiyingRightTabCheckBreak(this, {
          ziyingRightsCheckResult: this.ziyingRightsCheckResult,
          oldChannel,
          newChannel,
          showSpeed: this.showSpeed,
        });
        return;
      }

      /** h5外采活体、一键绑卡返回提现时，恢复原来选择状态 */
      if (!oldChannel && newChannel && (this.h5Bio || this.quickBindCardResult)) {
        // if (this.recordCountDownTime) {
        //   this.getRightSummaryData();
        // }
        return;
      }

      this.initSpeedStatus();

      // 切换产品时,将当前状态置为不勾选
      this.isChecked = false;
      this.updateLoanRightState({
        isRightTick: this.isChecked,
      });
    }

    private created() {
      /** 选银行卡、优惠劵返回后，若需要购买权益并有倒计时时间，重新请求权益接口 */
      // TODO: 暂时线上无该流程，无需考虑利率字段的传入
      // if (this.needRight && this.recordCountDownTime) {
      //   this.getRightSummaryData();
      // }
    }

    private mounted() {
      // 权益卡片初始化时,保持协议勾选框的状态与进入绑卡页一致
      this.isChecked = this.isRightTick || false;

      // 卡片曝光逻辑：在卡片初始化挂载时做对应的曝光
      this.$ubtModuleExposeNew('finance_loan_apply_right_expose', {
        ...this.tqParams,
        showSpeed: this.showSpeed,
      });
    }

    private beforeDestroy() {
      this.zxCardAppContractInstance.destroyInstance();

      destroyPopupA();
      destroyPopupB();
    }

    public destroyContract():void {
      this.zxCardAppContractInstance.destroyInstance();
    }

    public removeStorageKey(): void {
      this.updateLoanRightState({
        showSpeed: false,
        isRightTick: false,
        rightParams: null,
      });
    }

    /** 根据ab结果，悬旗右tab的文字 */
    private get tabRightText(): string | undefined {
      const { extraInfo } = this.rightSummary || {};
      if ([GalileoExpStyleEnum.STYLE_B,
        GalileoExpStyleEnum.STYLE_A].includes(extraInfo?.style as GalileoExpStyleEnum)) {
        return '普通借款';
      }
      return undefined;
    }

    /** 获取极速tab的文字，如果样式是A或B则返回"臻享服务"，否则返回null使用默认值 */
    private getSpeedTabText() {
      const { extraInfo } = this.rightSummary || {};
      if ([GalileoExpStyleEnum.STYLE_B,
        GalileoExpStyleEnum.STYLE_A].includes(extraInfo?.style as GalileoExpStyleEnum)) {
        return <SpeedTabText />;
      }
      return null; // 返回null时，插槽会使用默认内容
    }


    private renderCard() {
      const { extraInfo } = this.rightSummary || {};

      /** 样式A 只会影响自营臻享卡的展示 */
      if (extraInfo?.style === GalileoExpStyleEnum.STYLE_A) {
        return (
          <StyleA
            rightSummary={this.rightSummary}
            isRightDiscountUser={!!this.isRightDiscountUser}
            onTabChange={this.onTabChange.bind(this, SpeedRightsType.NORMAL)}
            onShowRightsDetail={this.showRightsDetail.bind(this)}
          />
        );
      }

      /** 样式B 只会影响联营臻享卡的展示 */
      if (extraInfo?.style === GalileoExpStyleEnum.STYLE_B) {
        return (
          <StyleB
            rightSummary={this.rightSummary}
            isRightDiscountUser={!!this.isRightDiscountUser}
            onTabChange={this.onTabChange.bind(this, SpeedRightsType.NORMAL)}
            onShowRightsDetail={this.showRightsDetail.bind(this)}
          />
        );
      }

      // 返回 null，使用插槽的默认内容（RightsCardContent）
      return null;
    }

    private render() {
      return this.showRights ? (
        <RightsTabContainer
          showSpeed={this.showSpeed}
          rightSummary={this.rightSummary}
          rightscheckTipsShow={this.rightscheckTipsShow}
          isChecked={this.isChecked}
          isRightDiscountUser={!!this.isRightDiscountUser}
          onCheckChange={this.checkChange.bind(this)}
          onOpenContract={this.openContract.bind(this)}
          onCardTabChange={this.onTabChange.bind(this, SpeedRightsType.NORMAL)}
          onShowRightsDetail={this.showRightsDetail.bind(this)}
        >
          <template slot="speedCard">
            {this.renderCard()}
          </template>
          <template slot="normalTab">
            <NormalTabContent
              rightSummary={this.rightSummary}
              tabRightText={this.tabRightText}
              onTabChange={this.onTabChange.bind(this, SpeedRightsType.SPEED)}
            >
              <template slot="speedTabText">
                {this.getSpeedTabText()}
              </template>
            </NormalTabContent>
          </template>
        </RightsTabContainer>
      ) : null;
    }
}
